import { MouseEvent, useCallback, useEffect, useMemo } from 'react'

import { useDeepCompare } from '@hozana/hooks/useDeepCompare'
import { usePrevious } from '@hozana/hooks/usePrevious'
import { TUrlObject, useRouter } from '@hozana/router'
import { getFirstQueryValue } from '@hozana/router/functions'

export type TUseTabsProps<D extends Record<string, string>> = {
  data: D
  /**
   * The default tab will be shown when no tab is defined in the url query.
   * If not specified, it will be the first tab in data.
   * */
  defaultTab?: keyof D
  onChange?: (tabId: keyof D) => void
  paramName?: string
}

export type TUseTabs<D extends Record<string, string>> = {
  currentTab: keyof D
  changeTab: (tabId: keyof D, e?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  getTabUrl: (tabId: keyof D) => TUrlObject
}

/**
 * ### useTabs ###
 */
export const useTabs = <D extends Record<string, string>>({
  paramName = 'tab',
  data,
  defaultTab = Object.keys(data)[0],
  onChange,
}: TUseTabsProps<D>): TUseTabs<D> => {
  const { pathname, query, replace } = useRouter() // #router.replace

  const [memoizedData] = useDeepCompare([data])

  const currentTab = useMemo(() => {
    const queryValue = getFirstQueryValue(query[paramName])
    return Object.keys(memoizedData).includes(queryValue) ? queryValue : defaultTab
  }, [defaultTab, memoizedData, paramName, query])

  const prevCurrentTab = usePrevious(currentTab) || currentTab

  const getTabUrl = useCallback(
    (tabId) => {
      const { [paramName]: _exTabId, ...remainingQuery } = query
      return {
        pathname,
        query: {
          ...remainingQuery,
          ...(tabId !== defaultTab && { [paramName]: tabId }),
        },
      }
    },
    [defaultTab, paramName, pathname, query],
  )

  useEffect(() => {
    if (onChange && currentTab !== prevCurrentTab) {
      onChange(currentTab)
    }
  }, [currentTab, onChange, prevCurrentTab])

  const changeTab = useCallback(
    (tabId: keyof D, e?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (e) {
        e.preventDefault()
      }
      if ((Object.keys(memoizedData) as (keyof D)[]).includes(tabId)) {
        onChange?.(tabId)
        replace(getTabUrl(tabId), undefined, { scroll: false })
      }
    },
    [getTabUrl, memoizedData, onChange, replace],
  )

  return { currentTab, changeTab, getTabUrl }
}
